import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import dynamic from 'next/dynamic';
import parse from 'html-react-parser';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {colors, borderRadius, fontSize, fontWeight, lineHeight, shadows, spacing} from '@/styles/tokens';
import Eyebrow from '@/components/Common/Eyebrow';

const DisplayImage = dynamic(import('@/components/Ui-components/display-image'), {ssr: false});

export interface CardTechnologyItem {
    icon: any;
    label: string;
}

export interface CardTechnologyCard {
    heading: string;
    description?: string;
    values: CardTechnologyItem[];
}

interface CardTechnologyProps {
    eyebrow?: string;
    heading: string;
    subheading?: string;
    data: CardTechnologyCard[];
    /**
     * Fixes the icons to this many per row on desktop instead of letting them
     * wrap freely. Mobile always falls back to a narrower grid.
     */
    iconsPerRow?: number;
    /** Icon box size, as a `spacing` token key. Defaults to 56. */
    iconSize?: keyof typeof spacing;
    /** Card side padding, as a `spacing` token key. Defaults to 24 (same as the vertical padding). */
    cardPaddingX?: keyof typeof spacing;
}

const cardSx = (paddingX: keyof typeof spacing) =>
    ({
        bgcolor: colors.background.default,
        borderRadius: borderRadius.xl,
        border: `1px solid ${colors.neutral.borderLight}`,
        boxShadow: shadows.sm,
        py: spacing[24],
        px: spacing[paddingX],
        height: '100%',
        display: 'flex',
        flexDirection: 'column' as const
    } as const);

const cardHeadingSx = {
    fontSize: fontSize[18],
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    lineHeight: lineHeight[28]
} as const;

const cardDescSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[28],
    mt: spacing[4]
} as const;

const iconContainerSx = (size: keyof typeof spacing) =>
    ({
        width: spacing[size],
        height: spacing[size],
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0
    } as const);

/** Free-wrapping icon strip — the default when `iconsPerRow` is not set. */
const iconsWrapSx = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing[12],
    alignContent: 'flex-start'
} as const;

const iconItemSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: spacing[72]
} as const;

/** Grid cells stretch, so the fixed item width would misalign the columns. */
const iconGridItemSx = {...iconItemSx, width: '100%'} as const;

const iconLabelSx = {
    fontSize: fontSize[14],
    color: colors.text.primary,
    textAlign: 'center' as const,
    mt: spacing[8],
    wordBreak: 'break-word' as const
} as const;

const CardTechnology = ({eyebrow, heading, subheading, data, iconsPerRow, iconSize = 56, cardPaddingX = 24}: CardTechnologyProps) => {
    const iconsLayoutSx = iconsPerRow
        ? {
              display: 'grid',
              gridTemplateColumns: {xs: 'repeat(3, 1fr)', sm: `repeat(${iconsPerRow}, 1fr)`},
              gap: spacing[12],
              alignContent: 'flex-start'
          }
        : iconsWrapSx;

    return (
        <Box>
            <MUIContainer>
                <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={5}>
                    {eyebrow && <Eyebrow text={eyebrow} />}
                    <Typography variant="h1" component="h2" mb={3} fontSize={{xs: 35, sm: 48}} className="font-headers">
                        {parse(heading)}
                    </Typography>
                    {subheading && <Typography className="font-regular-text">{parse(subheading)}</Typography>}
                </Box>

                <Grid container spacing={3} pb={spacing[8]}>
                    {data?.map((card, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <Box sx={cardSx(cardPaddingX)}>
                                <Box mb={spacing[20]}>
                                    <Typography component="h3" className="font-headers" sx={cardHeadingSx}>
                                        {parse(card.heading)}
                                    </Typography>
                                    {card.description && (
                                        <Typography className="font-regular-text" sx={cardDescSx}>
                                            {card.description}
                                        </Typography>
                                    )}
                                </Box>

                                <Box
                                    sx={{
                                        borderTop: `1px solid ${colors.neutral.borderLight}`,
                                        pt: spacing[20],
                                        flex: 1,
                                        ...iconsLayoutSx
                                    }}
                                >
                                    {card.values?.map((item, i) => (
                                        <Box key={i} sx={iconsPerRow ? iconGridItemSx : iconItemSx}>
                                            <Box sx={iconContainerSx(iconSize)}>
                                                <DisplayImage
                                                    src={item.icon}
                                                    alt={item.label}
                                                    fill
                                                    style={{objectFit: 'contain', padding: spacing[4]}}
                                                    priority={false}
                                                />
                                            </Box>
                                            <Typography sx={iconLabelSx}>{item.label}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </MUIContainer>
        </Box>
    );
};

export default memo(CardTechnology);
